import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import React from 'react'
import { Breadcrumbs } from '../components/Breadcrumbs'
import { CardView } from '../components/Card'
import { CardBox, CardBoxProps } from '../components/CardBox'
import { CardBoxGrid } from '../components/CardBoxGrid'
import { ContactNavigated } from '../components/ContactNavigated'
import { Content } from '../components/Content'
import { FlexiLink, FlexiLinkProps } from '../components/FlexiLink'
import { Intro } from '../components/Intro'
import { MissingContent } from '../components/MissingContent'
import { PhoneNumbers } from '../components/PhoneNumbers'
import { RadioButtons } from '../components/RadioButtons'
import { RegionMapProps } from '../components/RegionMap'
import { Seo } from '../components/Seo'
import { WidthBound } from '../components/WidthBound'
import { useCurrentLocale } from '../src/contexts/CurrentLanguageContext'
import { useLocalization } from '../src/hooks/useLocaleFormatter'
import { useTranslate } from '../src/hooks/useTranslate'
import {
	ContactOfficeFragment,
	ContactPersonFragment,
	ContactRegionFragment,
	PageFragment,
} from '../src/model/fragments'
import {
	DEFAULT_LOCALE,
	getLocale,
	getPossibleLocales,
	Locale,
} from '../src/model/locale'
import { assertNever } from '../src/utils/assertNever'
import { axiosGet } from '../src/utils/axiosWrapper'

const RegionMapDynamic = dynamic<RegionMapProps>(
	() => import('../components/RegionMap').then((mod) => mod.RegionMap),
	{ ssr: false },
)

export default function ContactMap({
	page,
	possibleLocales,
	contactRegions,
	contactOffices,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
	const locale = useCurrentLocale()
	const translate = useTranslate();
	const initialCountry = React.useMemo(
		() =>
			locale === Locale.cs ? 0 : locale === Locale.sk ? 1 : assertNever(locale),
		[locale],
	)
	const [countryFilter, setCountryFilter] = React.useState(initialCountry)
	const countryFilterISOCode =
		countryFilter === 0 ? 'CZ' : countryFilter === 1 ? 'SK' : 'CZ'

	const [activePerson, setActivePerson] =
		React.useState<null | ContactPersonFragment>(null)

	const [highlightedPerson, setHighlightedPerson] =
		React.useState<null | ContactPersonFragment>(null)
	const onUnselectHighlightedPerson = React.useCallback(
		() => {
			setHighlightedPerson(null);
		}, 
		[setHighlightedPerson],
	)
	const countryRegions = React.useMemo(
		() =>
			contactRegions.filter(
				(region) =>
					region.salesRepresentative !== undefined &&
					region.code.startsWith(countryFilterISOCode),
			),
		[contactRegions, countryFilterISOCode],
	)

	const allPersons = React.useMemo(
		() =>
			countryRegions
				.map((region) => region.salesRepresentative)
				.filter(person => person)
				// Filter unique
				.filter(
					(person, i, persons) =>
						persons.findIndex((a) => person.id === a.id) === i,
				),
		[countryRegions],
	)

	const allOffices = React.useMemo(
		() =>
			contactOffices.filter(
				(office) => office.country?.code === countryFilterISOCode,
			),
		[contactOffices, countryFilterISOCode],
	)

	if (!page) {
		return <MissingContent />
	}

	const data = getLocale(page.locales, possibleLocales)

	if (!data) {
		return <MissingContent />
	}

	return (
		<>
			<Seo seo={data.seo} />
			<Breadcrumbs
				items={[
					{
						label: data.title,
					},
				]}
			/>
			<WidthBound>
				<Intro title={data.title}>
					<Content content={data.content} innerSize="narrow" />
				</Intro>
			</WidthBound>
			<ContactNavigated current="contactMap" offices={allOffices}>
				<WidthBound>
					<RadioButtons
						initialIndex={initialCountry}
						items={[
							{
								text: 'Česká republika',
							},
							{
								text: 'Slovenská republika',
							},
						]}
						onChange={setCountryFilter}
					/>
					<h2>{translate('select-region-sales-specialist')}.</h2>

					<RegionMapDynamic
						coutryCode={countryFilterISOCode}
						regions={countryRegions}
						allRegions={contactRegions}
						highlightedPerson={highlightedPerson}
						setActivePerson={setActivePerson}
						onUnselectHighlightedPerson={onUnselectHighlightedPerson}
					/>

					<CardBoxGrid
						items={allPersons.map((person) => (
							<PersonCard
								person={person}
								onClick={() => {setHighlightedPerson(person);setActivePerson(person);}}
								isActive={activePerson?.id == person.id}
							/>
						))}
					/>
				</WidthBound>
			</ContactNavigated>
		</>
	)
}

export const PersonCard: React.FunctionComponent<{
	regionName?: string
	person: ContactPersonFragment
	forcePosition?: string
	onClick?: CardBoxProps['onClick'],
	isActive?: boolean
}> = ({ regionName, person, forcePosition, onClick, isActive }) => {
	const localization = useLocalization()
	return (
		<CardBox
			title={regionName || person.name}
			onClick={onClick}
			isActive={isActive}
			content={
				<>
					{regionName && <div>{person.name}</div>}
					{person.locales.length > 0 && (
						<div>{forcePosition ?? localization.extractLocale(person.locales)?.jobTitle}</div>
					)}
					{person.email && (
						<div>
							Email: <a href={`mailto:${person.email}`}>{person.email}</a>
						</div>
					)}
					{person.phone && (
						<div>
							Tel.: <PhoneNumbers numbers={person.phone} />
						</div>
					)}
					{person.link && (
						<div className='cardBox-textlink'>
							{<FlexiLink  {...(person.link as FlexiLinkProps)}>Přejít na detailní informace</FlexiLink> }
						</div>
					)}
				</>
			}
			view={CardView.small}
		/>
	)
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
	const locale = (ctx.query.locale as Locale) || DEFAULT_LOCALE
	const possibleLocales = getPossibleLocales(locale as Locale)
	const {
		getPage: page,
		listContactRegion: contactRegions,
		listContactOffice: contactOffices,
	} = await axiosGet('/contacts/list', {locale: locale, navigationId: ctx.query.id}) as 
	{
		getPage: PageFragment,
		listContactOffice: ContactOfficeFragment[],
		listContactRegion: ContactRegionFragment[]
	};

	return {
		props: {
			page,
			possibleLocales,
			contactRegions,
			contactOffices,
		},
	}
}
